import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { SecretStub } from "protogen/secrets_pb";
import SecretDialog from "../secrets/SecretDialog";
import { useRevealSecret, useListSecrets } from "services/secrets";
import SnackPack, { SnackbarMessage } from "../common/SnackPack";
import ConfirmationDialog, {
  useConfirmationDialog,
} from "../common/ConfirmationDialog";
import { EphemeralSecret } from "../secrets/types";
import { attachmentsToUploads } from "../creation/FileUploader";
import SecretList from "../secrets/SecretList";
import { Family } from "protogen/advisors_service_pb";
import { PanelProps } from "components/details-page/TabbedDetailsPanel";
import Loading from "components/common/Loading";
import SearchBar from "components/common/SearchBar";
import CheckedDropDown from "components/common/CheckedDropDown";
import {
  TYPE_FILTER_OPTIONS,
  SORT_OPTIONS,
  DROPDOWN_SX,
} from "components/secrets/Constants";
import useIsMobile from "components/hooks/useIsMobile";
import { Check } from "lucide-react";

interface Props extends PanelProps {
  title: string;
  family: Family;
}
export default ({ family }: Props) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackPack, setSnackPack] = useState<readonly SnackbarMessage[]>([]);
  const [query, setQuery] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState("title_asc");
  const { request } = useRevealSecret((r) => {
    const secret: EphemeralSecret = {
      ref: r?.secret?.ref,
      name: r?.secret?.name || "",
      valueType: r?.secret?.valueType || "",
      value: r.secretValue,
      attachments: r?.attachments || [],
      uploads: attachmentsToUploads(r?.attachments || []),
    };
    setSelectedSecret(secret);
    setDialogOpen(true);
  });
  const { request: listSecrets, data: secretsData } = useListSecrets();
  const confirmState = useConfirmationDialog();
  const [localSecrets, setLocalSecrets] = useState<SecretStub[]>([]);
  const [creators, setCreators] = useState<any[]>([]);
  const [selectedSecret, setSelectedSecret] = useState<EphemeralSecret | null>(
    null,
  );

  const onAccessed = (secret: SecretStub) => {
    request({ secretRef: secret.ref });
    setSnackPack((prev) => [
      ...prev,
      {
        message: `Access Recorded`,
        key: new Date().getTime(),
      },
    ]);
  };
  const handleClose = () => {
    setSelectedSecret(null);
    setDialogOpen(false);
  };

  useEffect(() => {
    const asyncFn = async () => {
      const response = await listSecrets({ familyRef: family.ref });
      setLocalSecrets(response?.secrets || []);
      setCreators(response?.creators || []);
      const secretRef = searchParams.get("secretRef");
      if (!secretRef) return;

      await request({ secretRef: secretRef });

      // Not required but nice to remove the query parameters to avoid popping up the secret if
      // user manually refreshes the page.
      searchParams.delete("secretRef");
      navigate("." + searchParams.toString(), { replace: true });
    };
    asyncFn();
  }, [family.ref]);

  const displayedSecrets = useMemo(() => {
    let result = [...localSecrets];
    if (query) {
      const lowerQuery = query.toLowerCase();
      result = result.filter((s) => s.name.toLowerCase().includes(lowerQuery));
    }
    if (selectedTypes.length > 0) {
      result = result.filter((s) => selectedTypes.includes(s.valueType));
    }
    result.sort((a, b) => {
      switch (sortBy) {
        case "title_desc":
          return b.name.localeCompare(a.name);
        case "newest":
          return Number(b.creationTimeSec - a.creationTimeSec);
        case "oldest":
          return Number(a.creationTimeSec - b.creationTimeSec);
        default:
          return a.name.localeCompare(b.name);
      }
    });
    return result;
  }, [localSecrets, query, selectedTypes, sortBy]);

  if (secretsData === undefined) {
    return <Loading />;
  }
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "16px",
        alignSelf: "stretch",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h3">Secrets</Typography>

        <Button variant="outlined" onClick={() => setDialogOpen(true)}>
          Add Secret
        </Button>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          width: "100%",
          gap: "8px",
        }}
      >
        <SearchBar
          fullWidth={isMobile}
          onQuery={setQuery}
          onKeyPress={true}
          sx={{ marginLeft: "0px", ...(isMobile && { width: "100%" }) }}
        />
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
          <CheckedDropDown
            sx={DROPDOWN_SX}
            options={TYPE_FILTER_OPTIONS}
            radio={true}
            onChange={(opts) =>
              setSelectedTypes(
                opts.filter((o) => o.value !== "all").map((o) => o.value),
              )
            }
            displayValue="Type"
            noOutline={true}
          />
          <CheckedDropDown
            sx={DROPDOWN_SX}
            options={SORT_OPTIONS}
            closeOnSelect={true}
            ActiveIcon={Check}
            onChange={(opts) => {
              if (opts.length > 0) setSortBy(opts[0].value);
            }}
            displayValue="Sort"
            noOutline={true}
          />
        </Box>
      </Box>
      <SecretList
        secrets={displayedSecrets}
        creators={creators}
        onClick={(secret: SecretStub) => {
          onAccessed(secret);
        }}
        emptyMessage={
          localSecrets.length > 0
            ? "No secrets match your filters."
            : "No secrets have been added yet."
        }
      />

      <SnackPack
        snackPack={snackPack}
        alertSeverity="warning"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
      <SecretDialog
        open={dialogOpen}
        family={family}
        onClose={handleClose}
        onCreate={(s) => {
          if (s) {
            setLocalSecrets((prevState) => [s, ...prevState]);
          }
          handleClose();
        }}
        onDelete={(ref) => {
          if (ref) {
            setLocalSecrets((prevState) => [
              ...prevState.filter((secret) => secret.ref !== ref),
            ]);
          }
          handleClose();
        }}
        onEdit={(s) => {
          if (s) {
            setLocalSecrets((prevState) => [
              ...prevState.map((secret) => (secret.ref === s.ref ? s : secret)),
            ]);
          }
          handleClose();
        }}
        isEdit={selectedSecret !== null}
        secret={selectedSecret}
      />
      <ConfirmationDialog {...confirmState.dialogProps} />
    </Box>
  );
};
